// src/reports/ReportListItem.jsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Paper, 
  Stack, 
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  useTheme 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FaEye,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaTag,
  FaUser,
  FaChevronRight,
} from 'react-icons/fa';
import { MdOutlineReportProblem } from 'react-icons/md';
import MediaPreview from '../utils/MediaPreview';

export default function ReportListItem({ report, index }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const getStatusColor = (status) => {
    if (status === 'Verified' || status === 'Resolved') return 'success';
    if (status === 'Pending') return 'warning';
    if (status === 'In Progress') return 'info';
    return 'error';
  };

  const getStatusIcon = (status) => {
    if (status === 'Verified' || status === 'Resolved') return '✅';
    if (status === 'Pending') return '⏳';
    if (status === 'In Progress') return '🔄';
    return '❌';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: '0 4px 20px rgba(46,125,50,0.15)',
          borderLeft: '4px solid #2e7d32',
        },
        borderLeft: '4px solid transparent',
        position: 'relative',
      }}
      onClick={() => navigate(`/reports/${report.report_id}/details`)}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', p: 2 }}>
        {/* Thumbnail */}
        <Box
          sx={{
            width: 120,
            height: 120,
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            mr: 2,
            position: 'relative',
            bgcolor: '#f5f5f5',
          }}
        >
          <MediaPreview
            src={report?.media_url}
            alt={report?.category || 'Report'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* Status Badge on thumbnail */}
          <Chip
            label={report.status}
            size="small"
            color={getStatusColor(report.status)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontWeight: 'bold',
              fontSize: '0.6rem',
              height: 20,
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={1}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  fontSize: '1rem',
                  color: '#1a1a2e',
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {report.category || 'Uncategorized Report'}
              </Typography>
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  sx={{
                    color: '#2e7d32',
                    '&:hover': {
                      bgcolor: 'rgba(46,125,50,0.1)',
                    },
                  }}
                >
                  <FaChevronRight size={16} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {report.description || 'No description provided'}
            </Typography>

            {/* Meta Information */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2,
              alignItems: 'center',
              pt: 0.5,
            }}>
              {/* Location */}
              {report.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FaMapMarkerAlt size={12} color="#666" />
                  <Typography variant="caption" color="text.secondary">
                    {report.location}
                  </Typography>
                </Box>
              )}

              {/* Date */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FaCalendarAlt size={12} color="#666" />
                <Typography variant="caption" color="text.secondary">
                  {report.timestamp
                    ? new Date(report.timestamp).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
              </Box>

              {/* Category Tag */}
              {report.category && (
                <Chip
                  icon={<FaTag size={10} />}
                  label={report.category}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: 'rgba(46,125,50,0.1)',
                    color: '#2e7d32',
                    '& .MuiChip-icon': {
                      fontSize: '0.6rem',
                      color: '#2e7d32',
                    },
                  }}
                />
              )}

              {/* Reporter */}
              {report.reporter_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FaUser size={12} color="#666" />
                  <Typography variant="caption" color="text.secondary">
                    {report.reporter_name}
                  </Typography>
                </Box>
              )}

              {/* Priority/Urgency */}
              {report.urgency && (
                <Chip
                  label={`🔴 ${report.urgency}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: report.urgency === 'High' ? 'rgba(244,67,54,0.1)' : 
                             report.urgency === 'Medium' ? 'rgba(255,152,0,0.1)' :
                             'rgba(76,175,80,0.1)',
                    color: report.urgency === 'High' ? '#d32f2f' : 
                           report.urgency === 'Medium' ? '#f57c00' :
                           '#388e3c',
                  }}
                />
              )}
            </Box>

            {/* Bottom Row - Quick Stats */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 0.5,
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {report.comments_count !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    💬 {report.comments_count} comments
                  </Typography>
                )}
                {report.upvotes !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    👍 {report.upvotes} upvotes
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                ID: #{report.report_id?.slice(0, 8) || 'N/A'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}